// =============================================================================
// FILE: packages/renderer/src/components/growth/GatekeeperAlerts.tsx
// PURPOSE: Master-Admin-only background poller + toast for the Founder's
//          Gatekeeper. Polls the blocked-attempt registry and raises a
//          real-time "Intruder Blocked" toast the moment a blacklisted firm
//          (e.g. AECOM, Suncor) tries to sign in. Feeds the sidebar badge via
//          the gatekeeperAlert store.
// =============================================================================

import { useEffect, useRef, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { ShieldAlert, X } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store.js';
import { isMasterAdmin } from '@bim/shared/config/masterKey.config.js';
import { useGatekeeperAlertStore, type GkAttempt } from '../../stores/gatekeeperAlert.store.js';

const POLL_MS = 20000;
const AUTO_DISMISS_MS = 7000;

function resolveBackend(): string {
  return (window as unknown as Record<string, unknown>).__BACKEND_URL__ as string
    ?? (import.meta.env.VITE_BACKEND_URL as string)
    ?? '';
}

export function GatekeeperAlerts(): JSX.Element | null {
  const userEmail = useAuthStore((s) => s.user?.email);
  const master = isMasterAdmin(userEmail);
  const ingest = useGatekeeperAlertStore((s) => s.ingest);
  const toast = useGatekeeperAlertStore((s) => s.toast);
  const dismissToast = useGatekeeperAlertStore((s) => s.dismissToast);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Poll the registry while a Master Admin is signed in.
  useEffect(() => {
    if (!master) return;
    let alive = true;
    const headers = { 'X-Admin-Email': userEmail ?? '' };
    const poll = async (): Promise<void> => {
      try {
        const res = await fetch(`${resolveBackend()}/api/admin/gatekeeper/registry`, { headers });
        if (!res.ok || !alive) return;
        const rows = await res.json() as GkAttempt[];
        if (alive && Array.isArray(rows)) ingest(rows);
      } catch { /* transient — retry next tick */ }
    };
    void poll();
    const t = setInterval(() => void poll(), POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [master, userEmail, ingest]);

  // Auto-dismiss the toast.
  useEffect(() => {
    if (!toast) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(dismissToast, AUTO_DISMISS_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [toast?.id, dismissToast]);

  if (!master || !toast) return null;

  const accent = '#f43f5e';
  return createPortal(
    <div
      data-testid="gatekeeper-alert-toast"
      style={{
        position: 'fixed', bottom: 28, right: 28, zIndex: 10001, width: 350,
        background: 'rgba(12,6,10,0.97)', border: `1px solid ${accent}44`, borderLeft: `3px solid ${accent}`,
        borderRadius: 10, boxShadow: `0 10px 40px rgba(0,0,0,0.7), 0 0 60px ${accent}18`, overflow: 'hidden',
        animation: 'gk-slide-in 0.35s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      <style>{`
        @keyframes gk-slide-in { from{transform:translateY(24px) scale(0.96); opacity:0;} to{transform:translateY(0) scale(1); opacity:1;} }
        @keyframes gk-drain { from{width:100%;} to{width:0%;} }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 12px 8px 14px' }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, marginTop: 1, background: `${accent}18`, border: `1px solid ${accent}44`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ShieldAlert style={{ width: 15, height: 15, color: accent }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9, letterSpacing: '0.20em', textTransform: 'uppercase', color: `${accent}cc`, marginBottom: 2 }}>
            Gatekeeper · Intruder Blocked
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'rgb(240,245,255)', lineHeight: 1.3, wordBreak: 'break-word' }}>
            {toast.email || toast.match_value}
          </div>
        </div>
        <button type="button" onClick={dismissToast} data-testid="gatekeeper-alert-dismiss"
          style={{ width: 22, height: 22, flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(148,163,184,0.5)', borderRadius: 4 }}>
          <X style={{ width: 11, height: 11 }} />
        </button>
      </div>
      <div style={{ padding: '0 14px 12px' }}>
        <div style={{ fontSize: 11.5, color: 'rgba(226,232,240,0.7)', lineHeight: 1.55 }}>
          A blocked <strong style={{ color: '#fda4af' }}>{toast.match_type}</strong> attempted to access the RCD² Engine
          {toast.match_value ? <> · <span style={{ fontFamily: 'monospace', color: '#fda4af' }}>{toast.match_value}</span></> : null}
          {toast.ip ? <span style={{ color: 'rgba(148,163,184,0.6)' }}> · {toast.ip}</span> : null}
        </div>
      </div>
      <div style={{ height: 2, background: `${accent}1a` }}>
        <div key={toast.id} style={{ height: '100%', background: `linear-gradient(90deg, ${accent}, ${accent}66)`, animation: `gk-drain ${AUTO_DISMISS_MS}ms linear forwards` }} />
      </div>
    </div>,
    document.body,
  );
}
